import calcFunctionInv from './gammaFn';

const BOLTZMANN = 0.000086173303;

/**
 * Refer to derating tool 'http://www.ti.com/support-quality/reliability/temperature-change-FIT.html`
 * @param Ea The Activation Energy
 * @param tempLowC The operating/usage temperature
 * @param tempHighC The temperature during stress tests
 * @returns {number} The temperature dependant acceleration factor
 */
const calcAccelFactor = (Ea, tempLowC, tempHighC) => {
  const tempLowK = 273.0 + tempLowC;
  const tempHighK = 273.0 + tempHighC;
  return Math.exp((Ea / BOLTZMANN) * ((1.0 / tempLowK) - (1.0 / tempHighK)));
};

/**
 * Calculates the chi-squared based on the given number of failure parameters.
 * @param confidence The confidence interval
 * @param numFailures The number of failures
 * @returns {number} The failure rate
 */
const calcChiSquared = (confidence, numFailures) => {
  const DoF = (2.0 * numFailures) + 2.0;
  const pValue = 1.0 - (confidence / 100.0);
  const data = {
    DoF,
    pValue,
    chiinv: calcFunctionInv(pValue, DoF),
  };
  return data.chiinv;
};

/**
 * Calculates the failure rate based on the given parameters
 * @param chiSquared The calculated chi-squared parameter value
 * @param accelFactor The calculated acceleration factor
 * @param testDurationHours The test duration in hours
 * @param sampleSize The sample size
 * @returns {number} The failure rate
 */
const calcFailureRate = (chiSquared, accelFactor, testDurationHours, sampleSize) => {
  return (chiSquared) / (2.0 * accelFactor * testDurationHours * sampleSize);
};

/**
 * Calculates the FIT from the failure rate
 * @param failureRate The calculated acceleration factor
 * @returns {number} The FIT
 */
const calcFIT = (failureRate) => {
  return ((10 ** 9) * failureRate);
};

/**
 * Computes the MTTF based on given failure rate.
 * @param failureRate The failure rate
 * @returns {string}
 */
const calcMTTF = (failureRate) => {
  return (1.0 / failureRate);
};

/**
 * Compute the temperature dependant acceleration factor, FIT rate and MTTF based on the given
 * parameter.
 * @param tempApplication The usage/operating temperature in deg Celcius
 * @param tempQualification The stress-test temperature in deg Celcius
 * @param activationEnergy The activation energy
 * @param testDurationHours The test duration in hours
 * @param numFailures The number of failures
 * @param sampleSize The number of samples tested
 * @param confidence The confidence interval
 * @returns {{AF: number, FIT: number, MTTF: string}} Temp-dependant failure values
 */

export const calcTempBasedFIT = (
  tempApplication,
  tempQualification,
  activationEnergy,
  testDurationHours,
  numFailures,
  sampleSize,
  confidence,
) => {
  const chiSquared = calcChiSquared(confidence, numFailures);
  const accelFactor = calcAccelFactor(activationEnergy, tempApplication, tempQualification);
  const failureRate = calcFailureRate(chiSquared, accelFactor, testDurationHours, sampleSize);
  const fit = calcFIT(failureRate);
  const mttf = calcMTTF(failureRate);
  const data = {
    tempApplication,
    tempQualification,
    activationEnergy,
    testDurationHours,
    numFailures,
    sampleSize,
    confidence,
    chiSquared,
    accelFactor,
    failureRate,
    fit,
    mttf,
  };
  return {
    failureRate,
    FIT: fit,
    MTTF: mttf,
    AF: accelFactor,
  };
};

export const calcReliabilityFromFIT = (fit, time) => {
  const thermalReliability = Math.exp(-fit * (10 ** -9) * time);
  return {
    thermalReliability,
  };
};
